import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { COMPANY, SOCIALS } from "@/data/turboData";
import { FOOTER } from "@/constants/testIds";
import { PlatformIcon } from "./PlatformIcon";

const Footer = () => (
  <footer className="relative bg-[var(--tt-navy)] text-white mt-24">
    {/* CTA strip */}
    <div className="mx-auto max-w-7xl px-6 -translate-y-1/2">
      <div className="rounded-3xl bg-gradient-to-br from-[var(--tt-green)] to-[var(--tt-green-2)] p-8 md:p-12 shadow-2xl shadow-green-900/20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h3 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
            Ready to ship? Let&apos;s get it moving.
          </h3>
          <p className="text-white/90 mt-2 max-w-lg">
            Instant quote in under 30 seconds. No account needed to price-check.
          </p>
        </div>
        <Link
          to="/quote"
          className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tt-navy)] px-6 py-3.5 font-semibold hover:bg-[var(--tt-bg)] transition"
        >
          Get a Quote <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>

    <div className="mx-auto max-w-7xl px-6 pt-4 pb-16 grid grid-cols-2 md:grid-cols-5 gap-10">
      <div className="col-span-2">
        <div className="flex items-center gap-3">
          <div className="font-display text-2xl font-bold">Turbo Transit</div>
          <span className="tt-stripe" aria-hidden="true"><span /></span>
        </div>
        <p className="mt-3 text-white/70 text-sm max-w-xs">
          A global shipping and logistics company connecting people and packages
          across 180+ countries.
        </p>
        <div className="mt-5 space-y-2.5 text-sm">
          <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-white/80 hover:text-white transition">
            <Phone className="h-4 w-4 text-[var(--tt-accent)]" /> {COMPANY.phone}
          </a>
          <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-white/80 hover:text-white transition">
            <Mail className="h-4 w-4 text-[var(--tt-accent)]" /> {COMPANY.email}
          </a>
          <div className="flex items-start gap-2 text-white/80">
            <MapPin className="h-4 w-4 text-[var(--tt-accent)] mt-0.5 shrink-0" />
            <span>{COMPANY.address}</span>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          {SOCIALS.map((s) => (
            <a
              key={s.key}
              href={s.url}
              data-testid={FOOTER.social(s.key)}
              aria-label={s.label}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-[var(--tt-accent)] transition"
            >
              <PlatformIcon platform={s.icon} className="h-4 w-4" />
            </a>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-4">Services</div>
        <ul className="space-y-2.5 text-sm text-white/80">
          <li><Link to="/services" className="hover:text-white">Turbo Air Express</Link></li>
          <li><Link to="/services" className="hover:text-white">Turbo Bulk Express</Link></li>
          <li><Link to="/services" className="hover:text-white">Standard International</Link></li>
          <li><Link to="/services" className="hover:text-white">Travel Bags</Link></li>
          <li><Link to="/services" className="hover:text-white">Customer Bulk Shipping</Link></li>
          <li><Link to="/fulfillment" className="hover:text-white text-[var(--tt-zam-orange)]">3PL &amp; Fulfillment →</Link></li>
        </ul>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-4">Company</div>
        <ul className="space-y-2.5 text-sm text-white/80">
          <li><Link to="/about" className="hover:text-white">About us</Link></li>
          <li><Link to="/quote" className="hover:text-white">Get a quote</Link></li>
          <li><Link to="/track" className="hover:text-white">Track shipment</Link></li>
          <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
        </ul>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-4">Stay in the loop</div>
        <p className="text-sm text-white/70 mb-3">Weekly rates and shipping tips.</p>
        <form
          onSubmit={(e) => e.preventDefault()}
          className="flex items-center gap-2 rounded-full bg-white/10 pl-3 pr-1 py-1 border border-white/10"
        >
          <input
            data-testid={FOOTER.newsletterInput}
            type="email"
            placeholder="you@email.com"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40 min-w-0"
          />
          <button
            data-testid={FOOTER.newsletterSubmit}
            type="submit"
            className="rounded-full bg-[var(--tt-green-2)] hover:bg-[var(--tt-green-dark)] px-3 py-1.5 text-xs font-semibold transition"
          >
            Join
          </button>
        </form>
        <p className="mt-3 text-[11px] text-white/40">No spam. Unsubscribe anytime.</p>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto max-w-7xl px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
        <div>© {new Date().getFullYear()} Turbo Transit. All rights reserved.</div>
        <div className="flex items-center gap-5">
          <a href="#" className="hover:text-white">Battery Policy</a>
          <a href="#" className="hover:text-white">Liquid Policy</a>
          <a href="#" className="hover:text-white">Limited Quantity</a>
          <a href="#" className="hover:text-white">Terms</a>
          <a href="#" className="hover:text-white">Privacy</a>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
